import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import ThalesTriangleDiagram, {
  ThalesCrossedDiagram,
} from "../../components/diagrams/ThalesTriangleDiagram";

export default function TriangleSimilarityPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        Trójkąty podobne mają takie same kąty, a długości ich odpowiadających
        sobie boków są w tej samej proporcji. Kolejność liter w zapisie
        podobieństwa wskazuje odpowiadające sobie wierzchołki:
      </p>
      <BlockMath math={String.raw`\triangle ABC\sim\triangle DEF`} />
      <BlockMath
        math={String.raw`A\leftrightarrow D,\quad B\leftrightarrow E,\quad C\leftrightarrow F`}
      />
      <BlockMath
        math={String.raw`\frac{|AB|}{|DE|}=\frac{|BC|}{|EF|}=\frac{|AC|}{|DF|}`}
      />
      <p>Podobieństwo możemy rozpoznać na trzy sposoby:</p>
      <ul>
        <li>
          <strong>kąt–kąt</strong> — dwa kąty jednego trójkąta są równe dwóm
          kątom drugiego; trzeci kąt także jest wtedy równy,
        </li>
        <li>
          <strong>bok–bok–bok</strong> — trzy pary boków są proporcjonalne,
        </li>
        <li>
          <strong>bok–kąt–bok</strong> — dwie pary boków są proporcjonalne,
          a kąty zawarte między nimi są równe.
        </li>
      </ul>
      <p>
        Gdy na rysunku występują proste równoległe, równe kąty często od razu
        dają podobne trójkąty. To właśnie sytuacja znana z twierdzenia Talesa:
      </p>
      <BlockMath math={String.raw`AB\parallel CD\quad\Longrightarrow\quad\triangle OAB\sim\triangle OCD`} />
      <ThalesTriangleDiagram showSimilarAngles />
      <p>
        Najbezpieczniej najpierw zaznaczyć równe kąty, następnie połączyć
        odpowiadające sobie wierzchołki i dopiero potem ułożyć proporcję.
        Odpowiadające boki leżą naprzeciw odpowiadających sobie kątów.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Które boki sobie odpowiadają?",
      description: (
        <span>
          Z zapisu <InlineMath math={String.raw`\triangle ABC\sim\triangle DEF`} />
          odczytujemy zgodność wierzchołków.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`A\leftrightarrow D,\quad B\leftrightarrow E,\quad C\leftrightarrow F`} />
          <BlockMath math={String.raw`AB\leftrightarrow DE,\quad BC\leftrightarrow EF,\quad AC\leftrightarrow DF`} />
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Proporcjonalne boki",
      description: (
        <span>
          Boki pierwszego trójkąta mają długości <InlineMath math="3,4,5" />,
          a drugiego <InlineMath math="6,8,10" />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`\frac63=\frac84=\frac{10}{5}=2`} />
          <p>
            Wszystkie boki drugiego trójkąta są dwa razy dłuższe, więc trójkąty
            są podobne w skali <InlineMath math="k=2" />.
          </p>
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Brakujący bok z proporcji",
      description: (
        <span>
          W podobnych trójkątach <InlineMath math="AB=3" /> odpowiada bokowi
          <InlineMath math="DE=6" />, a <InlineMath math="BC=4" /> odpowiada
          bokowi <InlineMath math="EF=x" />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`\frac{|DE|}{|AB|}=\frac{|EF|}{|BC|}`} />
          <BlockMath math={String.raw`\frac63=\frac{x}{4}\quad\Longrightarrow\quad x=8`} />
        </div>
      ),
    },
    {
      label: "Przykład 4",
      summary: "Tales jako podobieństwo trójkątów",
      description: (
        <span>
          Proste <InlineMath math={String.raw`AB\parallel CD`} /> wyznaczają
          podobne trójkąty <InlineMath math="OAB" /> i <InlineMath math="OCD" />.
        </span>
      ),
      content: (
        <div>
          <ThalesTriangleDiagram
            labels={{ topNear: "6", topFar: "9", bottomNear: "x", bottomFar: "15" }}
            nearRatio={6 / 15}
            showSimilarAngles
          />
          <BlockMath math={String.raw`\frac{|OA|}{|AC|}=\frac{|OB|}{|BD|}`} />
          <BlockMath math={String.raw`\frac{x}{15}=\frac69\quad\Longrightarrow\quad x=10`} />
        </div>
      ),
    },
    {
      label: "Przykład 5",
      summary: "Trójkąty po przeciwnych stronach punktu",
      description: (
        <span>
          Dwie proste przecinają się w punkcie <InlineMath math="O" /> i są
          przecięte dwiema prostymi równoległymi. Kąty przy
          <InlineMath math="O" /> są wierzchołkowe, a pozostałe pary kątów
          wynikają z równoległości.
        </span>
      ),
      content: (
        <div>
          <ThalesCrossedDiagram
            labels={{ OA: "20", OC: "16", OD: "10", OB: "8" }}
            showSimilarAngles
          />
          <BlockMath math={String.raw`\triangle AOD\sim\triangle COB`} />
          <BlockMath math={String.raw`\frac{|OA|}{|OC|}=\frac{|OD|}{|OB|}=\frac{|AD|}{|CB|}`} />
          <p>
            Położenie trójkątów na rysunku może być inne, ale sposób pracy
            pozostaje ten sam: równe kąty, zgodność wierzchołków i proporcja.
          </p>
        </div>
      ),
    },
  ];

  return (
    <TheoryExamplesRunner
      title="Podobieństwo trójkątów"
      intro={intro}
      examples={examples}
    />
  );
}
